import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, timer, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResearchSelectionService } from '../research/research-selection.service';

/**
 * Ceiling for a single timer delay.
 *
 * `setTimeout` (and therefore rxjs `timer`) fire *immediately* when the delay
 * exceeds 2^31-1 ms (~24.8 days). "Remember me" sessions last 30 days
 * (2,592,000,000 ms), so any schedule must be clamped below this bound and
 * re-armed when it elapses.
 */
const MAX_TIMER_MS = 2_000_000_000; // ~23.1 days

export interface AuthUser {
  id: string;
  username: string;
  full_name: string;
  role: string;
  token: string;
  annotator_id: string | null;
  /** Research workspace persisted server-side (`users.active_research_id`). */
  active_research_id?: string | null;
  expires_at?: string;
  remember_me?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private http = inject(HttpClient);
  private router = inject(Router);
  private researchSelection = inject(ResearchSelectionService);
  private readonly STORAGE_KEY = 'auth_user';

  currentUser = signal<AuthUser | null>(this.loadStoredUser());

  /** Research workspace the server has on record for this user. */
  readonly activeResearchId = computed(() => this.currentUser()?.active_research_id ?? null);
  private expirationTimer?: Subscription;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private refreshing = false;

  private get storage(): Storage {
    // Use sessionStorage for non-remembered sessions, localStorage for remembered
    const user = this.loadStoredUserRaw();
    if (user?.remember_me === false) {
      return sessionStorage;
    }
    return localStorage;
  }

  private loadStoredUserRaw(): AuthUser | null {
    // Try localStorage first, then sessionStorage
    for (const store of [localStorage, sessionStorage]) {
      try {
        const raw = store.getItem(this.STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          return {
            ...parsed,
            annotator_id: parsed.annotator_id ?? null,
          };
        }
      } catch {
        // Continue to next storage
      }
    }
    return null;
  }

  private loadStoredUser(): AuthUser | null {
    const user = this.loadStoredUserRaw();
    if (!user) return null;

    // Check if token is expired
    if (user.expires_at && new Date(user.expires_at) < new Date()) {
      // Token expired - clear storage
      this.clearAllStorage();
      return null;
    }

    // Start expiration timer for loaded user
    this.startExpirationTimer(user);
    this.startRefreshTimer(user);

    return user;
  }

  private clearAllStorage(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  private saveUser(user: AuthUser): void {
    const store = user.remember_me ? localStorage : sessionStorage;
    // Clear from other storage first
    if (user.remember_me) {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
    store.setItem(this.STORAGE_KEY, JSON.stringify(user));
    this.startExpirationTimer(user);
    this.startRefreshTimer(user);
  }

  /**
   * Proactively refresh the session token before it expires, so an active user
   * is never logged out mid-task. Refresh fires at 80% of the remaining
   * lifetime (minimum 60s). The server rotates the token and extends expiry.
   */
  private startRefreshTimer(user: AuthUser): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    if (!user.expires_at) return;

    const remaining = new Date(user.expires_at).getTime() - Date.now();
    if (remaining <= 0) return;
    const delay = Math.max(60_000, Math.floor(remaining * 0.8));

    // Clamp below the 32-bit timer ceiling — a 30-day "remember me" session
    // would otherwise overflow and fire immediately, logging the user out.
    this.refreshTimer = setTimeout(
      () => { void this.refreshSession(); },
      Math.min(delay, MAX_TIMER_MS),
    );
  }

  /** Rotate the token + extend expiry. Returns false when the session is gone. */
  async refreshSession(): Promise<boolean> {
    const user = this.currentUser();
    if (!user?.token || this.refreshing) return false;
    this.refreshing = true;
    try {
      const res = await firstValueFrom(
        this.http.post<{ token: string; expires_at: string; remember_me: boolean }>(
          `${environment.apiUrl}/users/refresh`, {}
        )
      );
      const updated: AuthUser = {
        ...user,
        token: res.token,
        expires_at: res.expires_at,
        remember_me: res.remember_me ?? user.remember_me,
      };
      this.saveUser(updated);
      this.currentUser.set(updated);
      return true;
    } catch (err: any) {
      if (err?.status === 401) this.logout();
      return false;
    } finally {
      this.refreshing = false;
    }
  }

  /**
   * Starts a timer that automatically logs out when token expires.
   * Also logs a warning 5 minutes before expiration.
   *
   * Long sessions (remember me = 30 days) exceed the 32-bit timer ceiling, so
   * we schedule in clamped steps and re-check rather than arming one giant
   * timer that would fire (and log out) immediately.
   */
  private startExpirationTimer(user: AuthUser): void {
    this.clearExpirationTimer();

    if (!user.expires_at) return;

    const expiresAt = new Date(user.expires_at).getTime();
    const warningTime = 5 * 60 * 1000; // 5 minutes warning
    let warned = false;

    const check = () => {
      const remaining = expiresAt - Date.now();

      if (remaining <= 0) {
        console.log('[Auth] Token expired, logging out...');
        this.logout();
        return;
      }

      if (!warned && remaining <= warningTime) {
        warned = true;
        console.warn('[Auth] Token expires in 5 minutes');
      }

      this.expirationTimer = timer(Math.min(remaining, MAX_TIMER_MS)).subscribe(check);
    };

    check();
  }

  private clearExpirationTimer(): void {
    if (this.expirationTimer) {
      this.expirationTimer.unsubscribe();
      this.expirationTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.clearExpirationTimer();
  }

  saveRedirect(url: string): void {
    if (url && url !== '/login') {
      localStorage.setItem('auth_redirect', url);
    }
  }

  async login(username: string, password: string, rememberMe = false): Promise<void> {
    const loginUrl = `${environment.apiUrl}/users/login?t=${Date.now()}`;
    const res = await firstValueFrom(
      this.http.post<{ user: any; token: string; expires_at: string; remember_me: boolean }>(
        loginUrl,
        { username, password, remember_me: rememberMe }
      )
    );
    const user: AuthUser = {
      id:            res.user.id,
      username:      res.user.username,
      full_name:     res.user.full_name ?? res.user.username,
      role:          res.user.role,
      token:         res.token,
      annotator_id:  res.user.annotator_id ?? null,
      active_research_id: res.user.active_research_id ?? null,
      expires_at:    res.expires_at,
      remember_me:   res.remember_me ?? rememberMe,
    };
    this.saveUser(user);
    this.currentUser.set(user);

    const redirect = localStorage.getItem('auth_redirect');
    localStorage.removeItem('auth_redirect');

    if (redirect) {
      this.router.navigateByUrl(redirect);
    } else {
      this.router.navigateByUrl(this.homeRoute());
    }
  }

  /**
   * Record the research workspace the server has persisted for this user.
   * Cached alongside the session so a restored page knows the selection
   * before `/users/me` resolves.
   */
  updateActiveResearch(id: string | null): void {
    const user = this.currentUser();
    if (!user || (user.active_research_id ?? null) === id) return;
    const updated: AuthUser = { ...user, active_research_id: id };
    this.saveUser(updated);
    this.currentUser.set(updated);
  }

  async validateToken(): Promise<boolean> {
    const user = this.currentUser();
    if (!user?.token) return false;

    // Check local expiration first
    if (user.expires_at && new Date(user.expires_at) < new Date()) {
      this.logout();
      return false;
    }

    try {
      const me = await firstValueFrom(
        this.http.get<{ active_research_id?: string | null }>(`${environment.apiUrl}/users/me`)
      );
      // The server is the source of truth for the workspace selection.
      this.updateActiveResearch(me?.active_research_id ?? null);
      return true;
    } catch (err: any) {
      // Only logout on 401 Unauthorized
      if (err?.status === 401) {
        this.logout();
      }
      return false;
    }
  }

  logout(): void {
    this.clearExpirationTimer();
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = undefined; }
    this.clearAllStorage();
    // The research selection is per-user server state; don't leak it to the
    // next account that signs in on this browser.
    this.researchSelection.set(null);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  get token(): string | null {
    return this.currentUser()?.token ?? null;
  }

  get isLoggedIn(): boolean {
    return this.currentUser() !== null;
  }

  get isAdmin(): boolean {
    const r = this.currentUser()?.role;
    return r === 'admin' || r === 'superadmin' || r === 'researcher';
  }

  get isSuperAdmin(): boolean {
    const r = this.currentUser()?.role;
    return r === 'superadmin' || r === 'admin';
  }

  get isResearcher(): boolean {
    return this.currentUser()?.role === 'researcher';
  }

  get isAnnotator(): boolean {
    return this.currentUser()?.role === 'annotator';
  }

  get isGuest(): boolean {
    return this.currentUser()?.role === 'guest';
  }

  /** Any authenticated role except guest. */
  get isMember(): boolean {
    return !!this.currentUser() && !this.isGuest;
  }

  /** Landing route after login — dataset overview for every role. */
  homeRoute(): string {
    const user = this.currentUser();
    if (!user) return '/login';
    return '/research/dataset/overview';
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${environment.apiUrl}/users/me/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    );
  }
}
